import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { searchAdminCompanies, fetchAdminCompanyInsights, updateAdminCompanyInsights } from '../../api/admin';
import { FiSearch, FiSave, FiPlus, FiX, FiLoader } from 'react-icons/fi';

const OVERVIEW_FIELDS = [
  { key: 'business_overview', label: 'Business Overview' },
  { key: 'product_segments', label: 'Product Segments' },
  { key: 'future_plans', label: 'Future Plans' },
  { key: 'clientele', label: 'Clientele' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'joint_ventures', label: 'Joint Ventures' },
  { key: 'subsidiaries', label: 'Subsidiaries' },
];

const MANAGEMENT_FIELDS = [
  { key: 'key_management_people_changes', label: 'Key Management Changes' },
  { key: 'management_guidance', label: 'Management Guidance' },
  { key: 'related_party_transactions', label: 'Related Party Transactions' },
];

const AdminCompanyInsights: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);

  // Search state
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Company data
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [insights, setInsights] = useState<Record<string, string>>({});
  const [pros, setPros] = useState<string[]>([]);
  const [cons, setCons] = useState<string[]>([]);
  const [originalData, setOriginalData] = useState<{ insights: Record<string, string>; pros: string[]; cons: string[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'management'>('overview');

  const [searching, setSearching] = useState(false);

  // Debounced search
  useEffect(() => {
    if (search.length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchAdminCompanies(search).then((res) => {
        setSearchResults(res.data.results || []);
        setSearchOpen(true);
      }).catch((err) => {
        console.error('Company search failed:', err);
        setSearchResults([]);
        setSaveResult({ type: 'error', message: `Search failed: ${err?.response?.data?.detail || err.message || 'Unknown error'}` });
      }).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Close dropdown on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const loadCompany = useCallback(async (name: string) => {
    setLoading(true);
    setSaveResult(null);
    try {
      const res = await fetchAdminCompanyInsights(name);
      const d = res.data;
      setInsights(d.insights || {});
      setPros(d.pros || []);
      setCons(d.cons || []);
      setOriginalData({ insights: { ...(d.insights || {}) }, pros: [...(d.pros || [])], cons: [...(d.cons || [])] });
      setSelectedCompany(d.company_name);
    } catch {
      setSaveResult({ type: 'error', message: 'Failed to load company data' });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = (name: string) => {
    setSearch(name);
    setSearchOpen(false);
    loadCompany(name);
  };

  const handleSave = async () => {
    if (!selectedCompany || !originalData) return;
    const payload: Record<string, any> = {};

    [...OVERVIEW_FIELDS, ...MANAGEMENT_FIELDS].forEach(({ key }) => {
      if ((insights[key] || '') !== (originalData.insights[key] || '')) {
        payload[key] = insights[key] || '';
      }
    });
    if (JSON.stringify(pros) !== JSON.stringify(originalData.pros)) payload.pros = pros;
    if (JSON.stringify(cons) !== JSON.stringify(originalData.cons)) payload.cons = cons;

    if (Object.keys(payload).length === 0) {
      setSaveResult({ type: 'error', message: 'No changes to save' });
      return;
    }

    setSaving(true);
    setSaveResult(null);
    try {
      const res = await updateAdminCompanyInsights(selectedCompany, payload);
      const d = res.data;
      const embeddedFields = d.embedding?.fields_embedded?.length || 0;
      setSaveResult({
        type: 'success',
        message: `Saved ${d.changes?.length || 0} field(s).${embeddedFields > 0 ? ` Re-embedded ${embeddedFields} vector(s).` : ''}`
      });
      setOriginalData({ insights: { ...insights }, pros: [...pros], cons: [...cons] });
    } catch {
      setSaveResult({ type: 'error', message: 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: `1px solid ${colors.grayScale400}`, backgroundColor: colors.inputBackground,
    color: colors.textColor, fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
  };

  const renderTextField = (key: string, label: string) => (
    <div key={key} className="mb-5">
      <label className="block mb-1.5 text-sm font-medium" style={{ color: colors.grayScale600 }}>{label}</label>
      <textarea
        rows={5}
        style={fieldStyle}
        value={insights[key] || ''}
        onChange={(e) => setInsights((prev) => ({ ...prev, [key]: e.target.value }))}
      />
    </div>
  );

  const renderListEditor = (label: string, items: string[], setItems: (v: string[]) => void) => (
    <div className="mb-5">
      <label className="block mb-1.5 text-sm font-medium" style={{ color: colors.grayScale600 }}>{label}</label>
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 mb-2">
          <textarea
            rows={2}
            style={{ ...fieldStyle, flex: 1 }}
            value={item}
            onChange={(e) => {
              const copy = [...items];
              copy[i] = e.target.value;
              setItems(copy);
            }}
          />
          <button
            onClick={() => setItems(items.filter((_, j) => j !== i))}
            className="p-2 rounded-lg mt-1"
            style={{ color: '#EF4444', backgroundColor: '#EF444415' }}
          >
            {React.createElement(FiX as any, { size: 16 })}
          </button>
        </div>
      ))}
      <button
        onClick={() => setItems([...items, ''])}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
        style={{ color: colors.primary, backgroundColor: `${colors.primary}15` }}
      >
        {React.createElement(FiPlus as any, { size: 14 })}
        Add
      </button>
    </div>
  );

  const tabs = [
    { id: 'overview' as const, label: 'Overview' },
    { id: 'management' as const, label: 'Management' },
  ];

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <h1 className="text-xl font-bold" style={{ color: colors.textColor }}>Company Insights</h1>

      {/* Search */}
      <div ref={searchRef} className="relative" style={{ maxWidth: 500 }}>
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl" style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.grayScale400}` }}>
          {searching
            ? React.createElement(FiLoader as any, { size: 18, color: colors.grayScale500, className: 'animate-spin' })
            : React.createElement(FiSearch as any, { size: 18, color: colors.grayScale500 })
          }
          <input
            type="text"
            placeholder="Search company by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: colors.textColor }}
          />
        </div>
        {searchOpen && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden shadow-lg z-50" style={{ backgroundColor: colors.cardBackground, border: `1px solid ${colors.grayScale400}`, maxHeight: 300, overflowY: 'auto' }}>
            {searchResults.map((c: any) => (
              <button
                key={c.name}
                onClick={() => handleSelect(c.name)}
                className="w-full text-left px-4 py-2.5 text-sm hover:opacity-80 transition"
                style={{ color: colors.textColor, borderBottom: `1px solid ${colors.grayScale200}` }}
              >
                <span className="font-medium">{c.name}</span>
                {c.sector && <span className="ml-2 text-xs" style={{ color: colors.grayScale500 }}>{c.sector}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-8 justify-center" style={{ color: colors.grayScale500 }}>
          {React.createElement(FiLoader as any, { size: 20, className: 'animate-spin' })}
          Loading...
        </div>
      )}

      {/* Result banner */}
      {saveResult && (
        <div className="px-4 py-3 rounded-xl text-sm font-medium" style={{
          backgroundColor: saveResult.type === 'success' ? '#10B98115' : '#EF444415',
          color: saveResult.type === 'success' ? '#10B981' : '#EF4444',
          border: `1px solid ${saveResult.type === 'success' ? '#10B98130' : '#EF444430'}`,
        }}>
          {saveResult.message}
        </div>
      )}

      {selectedCompany && !loading && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold" style={{ color: colors.textColor }}>{selectedCompany}</h2>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition"
              style={{ backgroundColor: colors.primary, color: '#fff', opacity: saving ? 0.6 : 1 }}
            >
              {saving
                ? <>{React.createElement(FiLoader as any, { size: 16, className: 'animate-spin' })} Saving & re-embedding...</>
                : <>{React.createElement(FiSave as any, { size: 16 })} Save Changes</>
              }
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-xl" style={{ backgroundColor: colors.inputBackground }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition"
                style={{
                  backgroundColor: activeTab === tab.id ? colors.cardBackground : 'transparent',
                  color: activeTab === tab.id ? colors.primary : colors.grayScale500,
                  boxShadow: activeTab === tab.id ? colors.shadowSoft : 'none',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="rounded-xl p-6" style={{ backgroundColor: colors.cardBackground, border: `1px solid ${colors.grayScale200}` }}>
            {activeTab === 'overview' && (
              <>
                {OVERVIEW_FIELDS.map(({ key, label }) => renderTextField(key, label))}
                {renderListEditor('Strengths (Pros)', pros, setPros)}
                {renderListEditor('Concerns (Cons)', cons, setCons)}
              </>
            )}
            {activeTab === 'management' && (
              <>
                {MANAGEMENT_FIELDS.map(({ key, label }) => renderTextField(key, label))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCompanyInsights;
